import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * React Router v5 `useHistory` compatibility shim built on v6/v7 `useNavigate`.
 *
 * React Router v7 removed `useHistory`. This exposes the slice of the v5 history
 * API the app uses — `push`, `replace`, `goBack`, `go` — backed by `useNavigate`.
 *
 * IMPORTANT: this intentionally does NOT call `useLocation()`. `useNavigate` is
 * stable and does not subscribe the component to location changes, so navigating
 * does not re-render every consumer. Components that need the current location
 * must call `useLocation()` directly (so only they re-render on navigation).
 *
 * It preserves the v5 convenience of passing navigation state inside the target
 * object (`push({ pathname, state })`); v7 takes `state` as a separate option.
 */
function splitTarget(to) {
  if (to && typeof to === 'object') {
    const { state, ...rest } = to;

    return { target: rest, state };
  }

  return { target: to, state: undefined };
}

export default function useHistory() {
  const navigate = useNavigate();

  return useMemo(
    () => ({
      push: (to, state) => {
        const split = splitTarget(to);
        const finalState = state !== undefined ? state : split.state;
        navigate(split.target, finalState !== undefined ? { state: finalState } : undefined);
      },
      replace: (to, state) => {
        const split = splitTarget(to);
        const finalState = state !== undefined ? state : split.state;
        navigate(split.target, { replace: true, ...(finalState !== undefined ? { state: finalState } : {}) });
      },
      goBack: () => navigate(-1),
      go: (delta) => navigate(delta),
    }),
    [navigate],
  );
}
